import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  initialMatterTabsState,
  isMatterTabUrl,
  matterTabsReducer,
  type MatterTabKey,
  type MatterTabsState,
  type ReviewHomeKind,
  type ReviewHomeSnapshot,
  type ReviewHomeTab,
} from './tabState';
import { describeTextContext, type TextContext } from './textContextActions';

export type OpenMatterInput = {
  matterId: number;
  title: string;
  url: string;
};

export type MatterContextMenuState = {
  clientX: number;
  clientY: number;
  matter?: OpenMatterInput;
  text?: TextContext;
};

export type DesktopMatterTabsContextValue = {
  enabled: boolean;
  state: MatterTabsState;
  contextMenu: MatterContextMenuState | null;
  registerHome: (home: ReviewHomeTab) => void;
  openMatterInBackground: (input: OpenMatterInput) => void;
  showContextMenu: (input: MatterContextMenuState) => void;
  dismissContextMenu: () => void;
  activate: (key: 'home' | MatterTabKey) => void;
  closeMatter: (matterId: number) => void;
  closeAll: () => void;
};

const DesktopMatterTabsContext = createContext<DesktopMatterTabsContextValue | null>(null);

function urlForLocation(location: ReturnType<typeof useLocation>): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

export function DesktopMatterTabsProvider({
  enabled,
  children,
}: {
  enabled: boolean;
  children: ReactNode;
}) {
  const [state, dispatch] = useReducer(matterTabsReducer, initialMatterTabsState);
  const [contextMenu, setContextMenu] = useState<MatterContextMenuState | null>(null);
  const stateRef = useRef(state);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    if (!enabled) return undefined;
    const showTextMenu = (event: MouseEvent) => {
      if (event.defaultPrevented) return;
      const text = describeTextContext(event.target, window.getSelection());
      if (!text) return;
      event.preventDefault();
      setContextMenu({ clientX: event.clientX, clientY: event.clientY, text });
    };
    document.addEventListener('contextmenu', showTextMenu);
    return () => document.removeEventListener('contextmenu', showTextMenu);
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !state.activeKey?.startsWith('matter:')) return;
    const matterId = Number(state.activeKey.slice('matter:'.length));
    const url = urlForLocation(location);
    if (isMatterTabUrl(url, matterId)) {
      dispatch({ type: 'UPDATE_MATTER_URL', matterId, url });
    }
  }, [enabled, location, state.activeKey]);

  const registerHome = useCallback((home: ReviewHomeTab) => {
    if (!enabled) return;
    dispatch({ type: 'REGISTER_HOME', home });
  }, [enabled]);

  const openMatterInBackground = useCallback((input: OpenMatterInput) => {
    if (!enabled || !stateRef.current.home || !isMatterTabUrl(input.url, input.matterId)) return;
    dispatch({ type: 'OPEN_MATTER_BACKGROUND', ...input });
  }, [enabled]);

  const showContextMenu = useCallback((input: MatterContextMenuState) => {
    if (!enabled) return;
    if (input.matter && !isMatterTabUrl(input.matter.url, input.matter.matterId)) return;
    if (!input.matter && !input.text) return;
    setContextMenu(input);
  }, [enabled]);

  const dismissContextMenu = useCallback(() => setContextMenu(null), []);

  const activate = useCallback((key: 'home' | MatterTabKey) => {
    if (!enabled) return;
    const current = stateRef.current;
    const destination = key === 'home'
      ? current.home?.url
      : current.matters.find((tab) => tab.key === key)?.url;
    if (!destination) return;
    dispatch({ type: 'ACTIVATE', key });
    void navigate(destination);
  }, [enabled, navigate]);

  const closeMatter = useCallback((matterId: number) => {
    if (!enabled) return;
    const current = stateRef.current;
    const next = matterTabsReducer(current, { type: 'CLOSE_MATTER', matterId });
    dispatch({ type: 'CLOSE_MATTER', matterId });
    if (current.activeKey !== `matter:${matterId}`) return;
    const destination = next.activeKey === 'home'
      ? next.home?.url
      : next.matters.find((tab) => tab.key === next.activeKey)?.url;
    if (destination) void navigate(destination);
  }, [enabled, navigate]);

  const closeAll = useCallback(() => {
    if (!enabled) return;
    const destination = stateRef.current.home?.url;
    dispatch({ type: 'CLOSE_ALL' });
    setContextMenu(null);
    if (destination) void navigate(destination);
  }, [enabled, navigate]);

  const value = useMemo<DesktopMatterTabsContextValue>(() => ({
    enabled,
    state: enabled ? state : initialMatterTabsState,
    contextMenu: enabled ? contextMenu : null,
    registerHome,
    openMatterInBackground,
    showContextMenu,
    dismissContextMenu,
    activate,
    closeMatter,
    closeAll,
  }), [
    activate,
    closeAll,
    closeMatter,
    contextMenu,
    dismissContextMenu,
    enabled,
    openMatterInBackground,
    registerHome,
    showContextMenu,
    state,
  ]);

  return (
    <DesktopMatterTabsContext.Provider value={value}>
      {children}
    </DesktopMatterTabsContext.Provider>
  );
}

// Context hooks intentionally share the provider module, matching AuthContext/FirmConfigContext.
// eslint-disable-next-line react-refresh/only-export-components
export function useDesktopMatterTabs(): DesktopMatterTabsContextValue {
  const context = useContext(DesktopMatterTabsContext);
  if (!context) throw new Error('useDesktopMatterTabs must be used within DesktopMatterTabsProvider');
  return context;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useMatterTabContextMenu(input: OpenMatterInput) {
  const tabs = useContext(DesktopMatterTabsContext);
  return useCallback((event: ReactMouseEvent<HTMLAnchorElement>) => {
    if (!tabs?.enabled) return;
    event.preventDefault();
    event.stopPropagation();
    const text = describeTextContext(event.target, window.getSelection());
    tabs.showContextMenu({ matter: input, text: text ?? undefined, clientX: event.clientX, clientY: event.clientY });
  }, [input, tabs]);
}

const ignoreHomeRegistration: (home: ReviewHomeTab) => void = () => undefined;

// eslint-disable-next-line react-refresh/only-export-components
export function useReviewHomeSnapshot(kind: ReviewHomeKind): {
  enabled: boolean;
  savedSnapshot: ReviewHomeSnapshot | null;
  registerHome: (home: ReviewHomeTab) => void;
} {
  const tabs = useContext(DesktopMatterTabsContext);
  const matchingHome = tabs?.enabled && tabs.state.home?.kind === kind ? tabs.state.home : null;
  return {
    enabled: Boolean(tabs?.enabled),
    savedSnapshot: matchingHome?.snapshot ?? null,
    registerHome: tabs?.registerHome ?? ignoreHomeRegistration,
  };
}
